/**
 * PostHog Analytics Event Taxonomy
 *
 * Naming convention: object:action (PostHog best practice)
 * All events are typed with their expected properties.
 */

// ---------------------------------------------------------------------------
// Tier 1: Frontend Events (posthog-js)
// ---------------------------------------------------------------------------

export const ANALYTICS_EVENTS = {
  // Navigation
  PAGE_VIEWED: 'page:viewed',
  TAB_SWITCHED: 'tab:switched',
  SEARCH_PERFORMED: 'search:performed',

  // Activation funnel
  // user:signed_up is SERVER-emitted (signup-notify.service.ts, at the
  // users-row first insert — the same created:true seam as the Slack notify),
  // so it fires exactly once per account regardless of how long onboarding
  // takes. The old web-side 10-min created_at heuristic undercounted.
  USER_SIGNED_UP: 'user:signed_up',
  // Session boundaries. signed_in fires on the null→user auth transition
  // (not on token refreshes); signed_out fires inside AuthContext.signOut —
  // the seam every sign-out path funnels through — right before the PostHog
  // identity reset.
  AUTH_SIGNED_IN: 'auth:signed_in',
  AUTH_SIGNED_OUT: 'auth:signed_out',

  // Task interactions
  // Client-perceived TTFT — one per user-send, stamped when the first agent
  // streaming delta renders. See apps/web helpers/perceivedTtft.ts.
  TASK_FIRST_TOKEN: 'task:first_token',
  TASK_RESUMED: 'task:resumed',
  TASK_TITLE_EDITED: 'task:title_edited',
  TASK_DESTROYED: 'task:destroyed',
  TASK_QUICK_ACTION: 'task:quick_action_selected',

  // Home briefing (HOME_BRIEFING flag)
  HOME_BRIEFING_VIEWED: 'home:briefing_viewed',
  HOME_NEED_OPENED: 'home:need_opened',
  HOME_NEEDS_ARRIVAL: 'home:needs_arrival',
  HOME_FIRST_RUN_STARTER: 'home:first_run_starter',
  // Bash home (HOME_BASH_V1 flag)
  HOME_TASK_ROW_OPEN: 'home:task_row_open',
  HOME_TAB: 'home:tab',
  HOME_RAIL_OPEN: 'home:rail_open',
  HOME_ARTIFACT_OPEN: 'home:artifact_open',
  HOME_CONNECT: 'home:connect',
  BOARD_DELTA_EMITTED: 'board:delta_emitted',
  /** Story spine: one task_story row minted (kind: phase|step). Meter-every-surface rule. */
  STORY_ENTRY_MINTED: 'story:entry_minted',

  // Agent interactions
  AGENT_INSTALLED: 'agent:installed',
  AGENT_UNINSTALLED: 'agent:uninstalled',
  AGENT_CREATED: 'agent:created',

  // Permission & approval
  CREDIT_APPROVAL_RESPONDED: 'credit:approval_responded',

  // Skills
  SKILL_INSTALLED: 'skill:installed',
  SKILL_CREATED: 'skill:created',

  // Routines
  ROUTINE_CREATED: 'routine:created',
  ROUTINE_TOGGLED: 'routine:toggled',
  ROUTINE_MANUALLY_TRIGGERED: 'routine:manually_triggered',

  // Files
  FILES_FILE_UPLOADED: 'files:file_uploaded',
  FILES_FILE_DOWNLOADED: 'files:file_downloaded',
  FILES_FOLDER_CREATED: 'files:folder_created',

  // Integrations
  INTEGRATION_DISCONNECTED: 'integration:disconnected',

  // Channels (Reach Bash anywhere) — waitlist for not-yet-live messaging surfaces
  CHANNEL_WAITLIST_JOINED: 'channel_waitlist_joined',

  // The waitlist (apps/web/src/components/waitlist): every new sign-up's front door until the new
  // product ships (spec 2026-10-17-waitlist-front-door). Booked calls are the goal.
  WAITLIST_VIEWED: 'waitlist:viewed',
  WAITLIST_STEP_ANSWERED: 'waitlist:step_answered',
  WAITLIST_CALL_CLICKED: 'waitlist:call_clicked',
  WAITLIST_CALL_BOOKED: 'waitlist:call_booked',
  WAITLIST_CODE_TRIED: 'waitlist:code_tried',

  // Credits / purchase journey. The web half: plan page view → checkout click
  // → gateway return. checkout:returned carries { outcome } —
  // 'success' | 'cancelled' (redirect rail ?status=…) or
  // 'paid_inline' | 'dismissed' (Razorpay in-page sheet) — one event, one
  // funnel step, however the customer came back.
  PLAN_PAGE_VIEWED: 'plan:page_viewed',
  PLAN_CHECKOUT_CLICKED: 'plan:checkout_clicked',
  CHECKOUT_RETURNED: 'checkout:returned',
  // Upgrade CTAs outside the plan page (credit-exhausted modal, credit bar).
  // Props: { source }.
  UPGRADE_CTA_CLICKED: 'upgrade:cta_clicked',
  // The server half: credit:purchase_initiated at checkout creation
  // (billing-checkout.handler), credit:purchase_completed at the charge-backed
  // webhook events (SUBSCRIPTION_ACTIVATED/RENEWED in applyGatewayEvent).
  CREDIT_PURCHASE_INITIATED: 'credit:purchase_initiated',
  CREDIT_PURCHASE_COMPLETED: 'credit:purchase_completed',
  CREDIT_PROMO_APPLIED: 'credit:promo_applied',
  // Composer/dock model pick — the CHOICE as an event (the model already rides
  // turn events as a property, which cannot measure picker usage/switching).
  // Props: { model, surface }.
  MODEL_SELECTED: 'model:selected',

  // Memory
  MEMORY_VIEWED: 'memory:viewed',
  MEMORY_EDITED: 'memory:edited',
  MEMORY_DELETED: 'memory:deleted',
  MEMORY_IMPORTED: 'memory:imported',

  // Nami (daily brief + weekly digest — A1 trust surfaces, P5)
  NAMI_BRIEF_SHOWN: 'nami:brief_shown',
  NAMI_BRIEF_DISMISSED: 'nami:brief_dismissed',
  NAMI_DIGEST_SHOWN: 'nami:digest_shown',
  NAMI_DIGEST_VERDICT: 'nami:digest_verdict',
  // Nami prep pack (A2 — first unsolicited surface, P6)
  NAMI_PREP_PACK_SHOWN: 'nami:prep_pack_shown',
  NAMI_PREP_PACK_RESOLVED: 'nami:prep_pack_resolved',
  // Nami's two routines (spec 2026-10-06 §7): Nami decides when, Bash does the work.
  // fired = a prep or brief task started; skipped = a timer that ran but fired nothing
  // (reason says why); delivered = the result reached the person; opened = they opened it.
  // Props: { kind: 'prep' | 'brief', routine_id?, job_key?, reason?, task_id?, surface? }.
  NAMI_ROUTINE_FIRED: 'nami:routine_fired',
  NAMI_ROUTINE_SKIPPED: 'nami:routine_skipped',
  NAMI_ROUTINE_DELIVERED: 'nami:routine_delivered',
  NAMI_ROUTINE_OPENED: 'nami:routine_opened',
  // Nami first-hand memory (P1 Doer/Keeper) — one per `remember` deposit, emitted
  // server-side from depositObservation. A deposit is a ZERO-cost fs append (the
  // tool call already rides the warm turn's billing), so it is metered for
  // per-user VISIBILITY/COUNT (how many first-hand facts a user's agent saves),
  // NOT a credit deduction. Props: { source, epistemic, task_id }.
  NAMI_FIRSTHAND_DEPOSIT: 'nami:firsthand_deposit',
  // Nami source quarantine (Task 5, real source tags): fired when a deposit's
  // wire `source` is PRESENT but neither a closed-union member nor the known
  // legacy literal ('task_firsthand') — an unrecognized producer or a
  // corrupted payload. The observation is NEVER deposited on this path
  // (quarantined, not defaulted): defaulting an off-union tag would silently
  // relabel it as a real authority-ladder rung, the exact bug the closed
  // union exists to cure. Props: { source, taskId }.
  NAMI_SOURCE_QUARANTINED: 'nami:source_quarantined',
  // Nami grounding rail: fired when a `procedure` claim arrives at the bottom
  // rung ('agent_inferred') and is DEMOTED to 'other' instead of entering the
  // procedures lane. A procedure is a standing instruction loaded before every
  // task, so it must rest on something the USER said or chose; the agent's own
  // behaviour is evidence about the agent. The observation still deposits (a
  // demotion, not a drop) — this counts the demotion so the guard's firing rate
  // is known, because a guard nobody can see firing is not a safety mechanism.
  // Props: { epistemic, taskId }.
  NAMI_UNGROUNDED_PROCEDURE: 'nami:ungrounded_procedure',

  // Account
  ACCOUNT_SETTINGS_CHANGED: 'account:settings_changed',
  SUPPORT_CONVERSATION_OPENED: 'support:conversation_opened',

  // ---------------------------------------------------------------------------
  // Tier 2: Server Events (posthog-node)
  // ---------------------------------------------------------------------------

  // Deploy traceability — one marker per server boot (commit_sha + release_env),
  // emitted by apps/server/src/services/deploy-marker.service.ts. Substitutes for
  // the PostHog annotations API (no key) so a deploy is bisectable in analytics
  // and in log_events.
  SERVER_DEPLOYED: 'server:deployed',

  // Signup attribution — server-emitted at the claim (attribution.service.ts).
  // attribution:claimed carries source/medium/campaign/invite_code plus
  // $set_once person props, so funnels can be cut by acquisition channel
  // regardless of the visitor's cookie-consent choice.
  ATTRIBUTION_CLAIMED: 'attribution:claimed',
  INVITE_LINK_ATTRIBUTED_SIGNUP: 'invite_link:attributed_signup',

  // Agent performance
  SERVER_TOOL_EXECUTED: 'server:tool_executed',
  // Task-birth SLI — ONE structured span per birth (wake-span.service.ts), emitted
  // when the birth's first token is known OR on a 120s give-up (outcome:'timeout').
  // Stitches the anchors the birth pipeline already logs (create → inbox_append →
  // relay_connected → turn_start → first_token) so a dashboard/alert catches a
  // task-birth latency regression instead of the founder's thumbs.
  TASK_BIRTH_SPAN: 'task:birth_span',
  // Composer M0 scoreboard: one per converse run, both arms. Props:
  // arm ('daemon'|'baseline'), ttc_ms (first commitment event), total_ms,
  // reason (done|error code), surface.
  // Value renamed 08-14 from 'conductor:turn' — PostHog insights over the
  // scoreboard must query BOTH names across the boundary (history keeps the
  // old value; new emits carry this one).
  COMPOSER_TURN: 'composer:turn',
  // obs-w4b: SERVER_TOOL_BLOCKED deleted — the tool permission/security gate is a
  // sandbox-side PreToolUse hook; its deny (PERMISSION_DENIED) is a trace-only side
  // channel never received server-side. The only server-side block is the credit
  // gate, already covered by SERVER_CREDIT_EXHAUSTED.

  // Credit system
  SERVER_CREDIT_DEDUCTED: 'server:credit_deducted',
  SERVER_CREDIT_EXHAUSTED: 'server:credit_exhausted',
  // Every credit_ledger GRANT site emits this one event with
  // { grant_type: 'signup_bonus' | 'plan_cycle' | 'connector_promo' |
  //   'invite_gift' | 'admin', amount } — the analytics twin of the ledger,
  // so earned credits are joinable to behavior without SQL.
  SERVER_CREDIT_GRANTED: 'server:credit_granted',
  // Subscription status transitions as applied from gateway webhooks
  // (applyGatewayEvent). Props: { status, event_type, gateway }. Cancels and
  // pauses become visible; churn stops being silence.
  SERVER_SUBSCRIPTION_STATUS_CHANGED: 'server:subscription_status_changed',

  // Account deletion lifecycle (account-routes.handler.ts). requested fires at
  // route entry, deleted after the six-step teardown succeeds, failed with
  // { reason } when it throws — the person record's last events instead of
  // silence indistinguishable from dormancy.
  ACCOUNT_DELETION_REQUESTED: 'account:deletion_requested',
  ACCOUNT_DELETED: 'account:deleted',
  ACCOUNT_DELETION_FAILED: 'account:deletion_failed',
  // obs-w4b: SERVER_CREDIT_WARNING deleted — there is no server-side threshold-
  // crossing event; `warn_balance` is a client-surfaced setting (Supabase Realtime),
  // so a "low balance" analytics event belongs to the web tier, not the server.

  // Sandbox lifecycle
  SERVER_SANDBOX_CREATED: 'server:sandbox_created',
  SERVER_SANDBOX_WOKEN: 'server:sandbox_woken',
  SERVER_SANDBOX_UPGRADED: 'server:sandbox_upgraded',
  SERVER_SANDBOX_ERROR: 'server:sandbox_error',
  SERVER_SANDBOX_WAKE_FAILED: 'server:sandbox_wake_failed',
  SERVER_SANDBOX_REPAIR: 'server:sandbox_repair',
  SERVER_SANDBOX_CIRCUIT_OPEN: 'server:sandbox_circuit_open',

  // Routine execution
  SERVER_ROUTINE_RUN_STARTED: 'server:routine_run_started',

  // Routine holds (spec 2026-09-09): a hold placed (mode act) or computed (mode shadow), and lifted.
  SERVER_ROUTINE_HELD: 'server:routine_held',
  SERVER_ROUTINE_RESUMED: 'server:routine_resumed',

  // Integration auth
  SERVER_INTEGRATION_AUTH_REQUIRED: 'server:integration_auth_required',
  SERVER_INTEGRATION_AUTH_COMPLETED: 'server:integration_auth_completed',

  // GTM Lead Engine (internal admin CRM). Props carry actor (operator user id)
  // for per-operator usage attribution, plus counts/credit estimates.
  GTM_LEAD_IMPORTED: 'gtm:lead_imported',
  GTM_LEAD_ENRICHED: 'gtm:lead_enriched',
  GTM_LEAD_DRAFTED: 'gtm:lead_drafted',
  GTM_LEAD_SENT: 'gtm:lead_sent',
  GTM_SEQUENCE_ENROLLED: 'gtm:sequence_enrolled',
  GTM_SEQUENCE_STEP_QUEUED: 'gtm:sequence_step_queued',
  GTM_SEQUENCE_PAUSED: 'gtm:sequence_paused',
  GTM_SEQUENCE_COMPLETED: 'gtm:sequence_completed',
  GTM_LEAD_REPLIED: 'gtm:lead_replied',
  GTM_CONTENT_SNIPED: 'gtm:content_sniped',
  GTM_CONTENT_ANALYZED: 'gtm:content_analyzed',
  GTM_INFLUENCER_ANALYZED: 'gtm:influencer_analyzed',

  // Cobrowsing lifecycle + QoS ([ow1] omni-observability OD5). ALL server-emitted
  // (only the server holds the Supabase user UUID); every call site lives in
  // apps/server/src/services/cobrowse-telemetry.ts, gated on COBROWSE_TELEMETRY_V1
  // plus the event's live feature flag (BROWSER_VIEWER_LEASE for the stream family,
  // BROWSER_SHARED_CONTROL for the control family). The ~1Hz BROWSER_VIEWING
  // heartbeat is NEVER captured per-beat (viewing_started rides the ≥10s-gap seam);
  // frame QoS is sampled (COBROWSE_QOS_SAMPLE_RATE, default 0.1).
  COBROWSE_SESSION_OPENED: 'cobrowse:session_opened',
  COBROWSE_VIEWING_STARTED: 'cobrowse:viewing_started',
  COBROWSE_STREAM_SUSPENDED: 'cobrowse:stream_suspended',
  COBROWSE_STREAM_RESUMED: 'cobrowse:stream_resumed',
  COBROWSE_CONTROL_USER_TOOK: 'cobrowse:control_user_took',
  COBROWSE_CONTROL_YIELDED: 'cobrowse:control_yielded',
  COBROWSE_AGENT_DEFERRED: 'cobrowse:agent_deferred',
  COBROWSE_FRAME_QOS: 'cobrowse:frame_qos',

  // ---------------------------------------------------------------------------
  // Tier 3: Sandbox Events (relayed via server)
  // ---------------------------------------------------------------------------
  // obs-w4b: SANDBOX_CONTEXT_LOST and SANDBOX_SECURITY_BLOCKED deleted — both are
  // sandbox-side conditions with no server-observable equivalent on the relay, so
  // they could never be wired here. SANDBOX_SUBAGENT_SPAWNED stays (the relay does
  // see SUBAGENT_SPAWNED and fires it).

  SANDBOX_SUBAGENT_SPAWNED: 'sandbox:subagent_spawned',

  // OS-hands per-verb usage — SERVER-emitted at the hands-audit seam
  // (appendHandsAudit kind 'action_report'), the one rail every gated verb
  // lane already meters through, so it needs no desktop release to ship.
  // Structural props only: { tool, verb, app, rung, lane, latency_ms,
  // outcome, code } — never free-form content (S2a doctrine).
  HANDS_VERB_EXECUTED: 'hands:verb_executed',

  // ---------------------------------------------------------------------------
  // [ow3] Telemetry uplink — dark client surfaces (extension/notch/desktop/kernel)
  // reach PostHog + log_events through the authed POST /api/telemetry fan-out, not
  // posthog-js. distinct_id is always the authed userId (never client-sent). These
  // are the v1 proof surface; more land as adapters do.
  // ---------------------------------------------------------------------------
  EXTENSION_PANEL_OPENED: 'extension:panel_opened',
  // [ow5] Desktop shell (Tauri/Rust) lifecycle — emitted by the native uplink in
  // apps/desktop/src-tauri/src/telemetry.rs, which mirrors these literals as Rust
  // consts (keep the two lists in lockstep; an unmirrored name is dropped at ingest).
  // exit_class prop: 'clean' (exit 0 / quit-initiated SIGTERM) | 'error' (non-zero
  // exit or unexpected death) | 'killed' (signal death).
  DESKTOP_APP_LAUNCHED: 'desktop:app_launched',
  DESKTOP_RUNNER_STARTED: 'desktop:runner_started',
  DESKTOP_RUNNER_EXITED: 'desktop:runner_exited',
  DESKTOP_HANDS_SPAWNED: 'desktop:hands_spawned',
  DESKTOP_HANDS_EXITED: 'desktop:hands_exited',
  DESKTOP_NOTCH_CONVERSE_RESULT: 'desktop:notch_converse_result',
  // Meta-event the telemetry client emits when its bounded queue drops the oldest
  // events under back-pressure (mirror precedent: mirror_overflow_dropped). Carries
  // { dropped: <count> } so a lossy client is loud, never silent.
  TELEMETRY_OVERFLOW_DROPPED: 'telemetry:overflow_dropped',
  // Connection-truth rail — health transitions of the web client's evidence-based
  // connection state (healthy/degraded/down). Props: { from, to, ms_since_inbound,
  // rtt_ms } — lets degraded-state frequency be observed in the field.
  CONNECTION_HEALTH_CHANGED: 'connection:health_changed',
  // Flash recorder — the web's report of a visible whole-screen disruption
  // (a big DOM swap, a layout shift, an automatic reload, a gate takeover, an
  // error boundary, a theme flip, a shell remount), with the run-up from the
  // flash-trace ring. Emitted by apps/web/src/lib/flash-report.ts, capped at 20
  // per page session. Props: { kind, detail, path (pathname only), visible,
  // sinceLoadMs, sinceVisibleMs, sinceFocusMs, sinceInputMs, host ('native' in
  // the desktop shell | 'web'), build, triggers, ring, removed?, added?,
  // root_removed?, root_added?, shift_source?, auto? }.
  SCREEN_FLASHED: 'screen:flashed',

  // ---------------------------------------------------------------------------
  // Vestra Analytics v1 — the typed catalog. Every name here has its property
  // shape in v1-events.ts (AnalyticsV1EventProperties); web and server emit
  // them through that type, never a free-form property bag. USER_SIGNED_UP,
  // SKILL_INSTALLED, HOME_ARTIFACT_OPEN and TASK_FIRST_TOKEN above are v1
  // events too — they kept their names and took v1 properties.
  //
  // Retired in the same change, so no action ever sends both names:
  //   onboarding:started, onboarding:step_completed → onboarding:screen_viewed
  //   onboarding:heard_about_answered → onboarding:step_submitted
  //   task:message_sent, home:dock_send, task:created → delegation:submitted
  //   server:task_started → delegation:started
  //   server:task_completed, server:task_errored, server:task_timed_out,
  //     server:turn_completed → delegation:finished
  //   permission:responded, question:answered, home:need_resolved → interaction:resolved
  //   composer:line → voice:session_ended
  //   server:routine_run_completed, server:routine_run_failed → routine:run_finished
  //   integration:connected (web) → integration:connection_confirmed (server)
  // ---------------------------------------------------------------------------
  AUTH_FLOW_STARTED: 'auth:flow_started',
  MARKETING_CTA_CLICKED: 'marketing:cta_clicked',
  ONBOARDING_SCREEN_VIEWED: 'onboarding:screen_viewed',
  ONBOARDING_STEP_SUBMITTED: 'onboarding:step_submitted',
  ONBOARDING_COMPLETED: 'onboarding:completed',
  DELEGATION_SUBMITTED: 'delegation:submitted',
  DELEGATION_ACCEPTED: 'delegation:accepted',
  DELEGATION_STARTED: 'delegation:started',
  DELEGATION_FINISHED: 'delegation:finished',
  DELEGATION_RESULT_PRESENTED: 'delegation:result_presented',
  INTERACTION_REQUESTED: 'interaction:requested',
  INTERACTION_RESOLVED: 'interaction:resolved',
  VOICE_SESSION_ENDED: 'voice:session_ended',
  ROUTINE_RUN_FINISHED: 'routine:run_finished',
  INTEGRATION_CONNECT_STARTED: 'integration:connect_started',
  INTEGRATION_CONNECTION_CONFIRMED: 'integration:connection_confirmed',
  INTEGRATION_FIRST_USED: 'integration:first_used',
  ABILITY_FIRST_USED: 'ability:first_used',
  CREATION_CREATED: 'creation:created',
  NAMI_PROPOSAL_RESOLVED: 'nami:proposal_resolved',
  CHANNEL_LINKED: 'channel:linked',
  CHANNEL_RESPONSE_SENT: 'channel:response_sent',
  CHANNEL_RESPONSE_FAILED: 'channel:response_failed',
  // Schema only: no emitter yet (the activation definition is still open).
  USER_ACTIVATION_ACHIEVED: 'user:activation_achieved',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
